import { api } from '../api';

export interface Language {
  id: number;
  code: string;
  name: string;
  native_name: string;
  flag: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface TranslationsResponse {
  data: {
    language: {
      code: string;
      name: string;
      native_name: string;
      flag: string;
    };
    translations: Record<string, unknown>;
  };
}

export interface LanguagesResponse {
  data: Language[];
}

// Mapeamento de códigos legados para códigos da API
const LANGUAGE_CODE_MAP: Record<string, string> = {
  br: 'pt-BR',
  en: 'en-US',
  pt: 'pt-BR',
  'pt-BR': 'pt-BR',
  'en-US': 'en-US',
};

// Mapeamento reverso
const REVERSE_CODE_MAP: Record<string, string> = {
  'pt-BR': 'br',
  'en-US': 'en',
};

export const mapToApiCode = (code: string): string =>
  LANGUAGE_CODE_MAP[code] || code;

export const mapFromApiCode = (code: string): string =>
  REVERSE_CODE_MAP[code] || code;

// Cache para traduções
const translationsCache: Record<string, Record<string, unknown>> = {};
const languagesCache: { data: Language[] | null; timestamp: number } = {
  data: null,
  timestamp: 0,
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export const getLanguages = async (): Promise<Language[]> => {
  // Verificar cache
  if (
    languagesCache.data &&
    Date.now() - languagesCache.timestamp < CACHE_DURATION
  ) {
    return languagesCache.data;
  }

  try {
    const response = await api.get<LanguagesResponse>('/languages');
    languagesCache.data = response.data.data;
    languagesCache.timestamp = Date.now();
    return response.data.data;
  } catch (error) {
    console.error('Erro ao buscar idiomas:', error);
    // Retornar idiomas padrão em caso de erro
    return [
      {
        id: 1,
        code: 'pt-BR',
        name: 'Portuguese (Brazil)',
        native_name: 'Português (Brasil)',
        flag: '🇧🇷',
        is_default: true,
        is_active: true,
        sort_order: 1,
      },
      {
        id: 2,
        code: 'en-US',
        name: 'English (US)',
        native_name: 'English (US)',
        flag: '🇺🇸',
        is_default: false,
        is_active: true,
        sort_order: 2,
      },
    ];
  }
};

export const getDefaultLanguage = async (): Promise<Language | null> => {
  try {
    const response = await api.get<{ data: Language }>('/languages/default');
    return response.data.data;
  } catch (error) {
    console.error('Erro ao buscar idioma padrão:', error);
    return null;
  }
};

export const getTranslations = async (
  languageCode: string,
  group = 'play',
): Promise<Record<string, unknown>> => {
  const apiCode = mapToApiCode(languageCode);
  const cacheKey = `${apiCode}_${group}`;

  // Verificar cache
  if (translationsCache[cacheKey]) {
    return translationsCache[cacheKey];
  }

  try {
    const response = await api.get<TranslationsResponse>(
      `/languages/${apiCode}/translations`,
      { params: { group } },
    );

    const { translations } = response.data.data;
    translationsCache[cacheKey] = translations;
    return translations;
  } catch (error) {
    console.error(`Erro ao buscar traduções para ${languageCode}:`, error);
    throw error;
  }
};

export const clearTranslationsCache = (): void => {
  Object.keys(translationsCache).forEach((key) => {
    delete translationsCache[key];
  });
  languagesCache.data = null;
  languagesCache.timestamp = 0;
};

export default {
  getLanguages,
  getDefaultLanguage,
  getTranslations,
  clearTranslationsCache,
  mapToApiCode,
  mapFromApiCode,
};
